/**
 * 打印纸面骨架：去掉浏览器自带的页眉/页脚（日期、文档标题、URL）。
 *
 * 原理：Chrome 只在 @page margin 区域内绘制页眉页脚，`@page { margin: 0 }`
 * 后无处可画，等于代码层面永久关闭「页眉和页脚」，不依赖用户去打印
 * 对话框里取消勾选。
 *
 * 代价是页边距不能再靠 @page 给，改由两部分补回：
 *   - 左右：body 的水平 padding（对每一页都生效）；
 *   - 上下：包裹内容的 table.pp-paper 的 thead/tfoot 空行 —— 打印时
 *     thead/tfoot 会在每一页重复，正好充当每页的上下留白。
 *     屏幕预览上隐藏这两条空行（body 已有四周 padding）。
 */

/** 附加在各渲染器 CSS 末尾（必须在全局 th/td 边框规则之后，保证覆盖）。 */
export function printPageChromeCss(marginMm: number): string {
  return `
    table.pp-paper { width: 100%; border-collapse: collapse; }
    table.pp-paper > thead > tr > td,
    table.pp-paper > tbody > tr > td,
    table.pp-paper > tfoot > tr > td { border: none; padding: 0; background: none; }
    table.pp-paper > tbody > tr { page-break-inside: auto; }
    thead.pp-vpad, tfoot.pp-vpad { display: none; }
    .pp-vpad td { height: ${marginMm}mm; }
    @media print {
      body { padding: 0 ${marginMm}mm; }
      thead.pp-vpad { display: table-header-group; }
      tfoot.pp-vpad { display: table-footer-group; }
    }
  `;
}

/** 把正文包进纸面骨架。页眉/页脚放进 thead/tfoot 的留白区，逐页重复。 */
export function wrapPrintBody(
  innerHtml: string,
  opts?: { headerText?: string; footerText?: string },
): string {
  const esc = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const head = (opts?.headerText || '').trim();
  const foot = (opts?.footerText || '').trim();
  const headCell = head
    ? `<div style="font-size:11px;color:#888;display:flex;align-items:flex-start;justify-content:center;height:100%;padding-top:2mm;">${esc(head)}</div>`
    : '';
  const footCell = foot
    ? `<div style="font-size:11px;color:#888;display:flex;align-items:flex-end;justify-content:center;height:100%;padding-bottom:2mm;">${esc(foot)}</div>`
    : '';
  return `<table class="pp-paper">
<thead class="pp-vpad"><tr><td>${headCell}</td></tr></thead>
<tbody><tr><td>
${innerHtml}
</td></tr></tbody>
<tfoot class="pp-vpad"><tr><td>${footCell}</td></tr></tfoot>
</table>`;
}
